import { ArrowRight, LockKeyhole, ScanFace, SlidersHorizontal, UsersRound } from 'lucide-react';
import { useEffect } from 'react';

import { parentSiteUrl, siteOrigin } from './siteLinks';
import { localizedRouteFor, resolveSiteRoute, routeForKey } from './siteRoutes';
import { interpolateSiteMessage, siteUiMessages } from './siteUiMessages';
import { ThemeControl } from '../theme/ThemeControl';

import type { DocumentPageContent } from './legalContent';
import type { LandingPageContent, SiteRoute } from './siteRoutes';
import type { SiteUiMessages } from './siteUiMessages';
import type { Locale } from '../i18n/types';
import type { ThemePreference } from '../theme/useTheme';
import type { ReactElement } from 'react';

const legalPageKeys = ['privacy', 'terms'] as const;

function absoluteUrl(path: string): string {
    return new URL(path, siteOrigin).href;
}

function formatRevisionDate(isoDate: string, locale: Locale): string {
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(isoDate));
}

function setMetaContent(selector: string, attribute: 'name' | 'property', content: string): void {
    let meta = document.head.querySelector<HTMLMetaElement>(selector);
    if (meta === null) {
        meta = document.createElement('meta');
        const selectorValue = selector.match(/"([^"]+)"/)?.[1];
        if (selectorValue !== undefined) meta.setAttribute(attribute, selectorValue);
        document.head.append(meta);
    }
    meta.content = content;
}

function synchronizeMetadata(route: SiteRoute): void {
    document.title = route.content.seoTitle;
    document.documentElement.lang = route.locale;
    setMetaContent('meta[name="description"]', 'name', route.content.seoDescription);
    setMetaContent(
        'meta[name="robots"]',
        'name',
        route.indexable ? 'index,follow,max-image-preview:large' : 'noindex,follow'
    );
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical === null) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.append(canonical);
    }
    canonical.href = absoluteUrl(route.path);
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => {
        link.remove();
    });
    (['en', 'tr'] as const).forEach((locale) => {
        const alternate = document.createElement('link');
        alternate.rel = 'alternate';
        alternate.hreflang = locale;
        alternate.href = absoluteUrl(localizedRouteFor(route, locale).path);
        document.head.append(alternate);
    });
    const defaultAlternate = document.createElement('link');
    defaultAlternate.rel = 'alternate';
    defaultAlternate.hreflang = 'x-default';
    defaultAlternate.href = absoluteUrl(localizedRouteFor(route, 'en').path);
    document.head.append(defaultAlternate);
}

function LandingPage({
    route,
    content,
    messages,
    toolHref,
    homeHref,
}: {
    route: SiteRoute;
    content: LandingPageContent;
    messages: SiteUiMessages;
    toolHref: string;
    homeHref: string;
}): ReactElement {
    return (
        <>
            <section className='site-hero'>
                <div>
                    <span className='eyebrow'>{content.eyebrow}</span>
                    <h1>{content.title}</h1>
                    <p>{content.description}</p>
                    <div className='site-actions'>
                        <a
                            className='site-button'
                            href={route.key === 'home' ? toolHref : homeHref}
                        >
                            {content.primaryAction} <ArrowRight aria-hidden='true' />
                        </a>
                        {route.key === 'notFound' && (
                            <a
                                className='site-text-link'
                                href={toolHref}
                            >
                                {messages.notFoundTool}
                            </a>
                        )}
                    </div>
                </div>
                <div
                    className='site-visual'
                    aria-hidden='true'
                >
                    <div className='scan-grid' />
                    <div className='scan-card scan-card--reference'>
                        <ScanFace />
                    </div>
                    <div className='scan-card scan-card--target'>
                        <UsersRound />
                        <span className='scan-box scan-box--one' />
                        <span className='scan-box scan-box--two' />
                        <span className='scan-box scan-box--three' />
                    </div>
                    <span className='scan-connector'>×</span>
                </div>
            </section>

            {route.key === 'home' && (
                <section className='site-features'>
                    <h2>{messages.featureHeading}</h2>
                    <div className='site-feature-grid'>
                        <article>
                            <LockKeyhole aria-hidden='true' />
                            <h3>{content.privacyTitle}</h3>
                            <p>{content.privacyDescription}</p>
                        </article>
                        <article>
                            <SlidersHorizontal aria-hidden='true' />
                            <h3>{content.modelsTitle}</h3>
                            <p>{content.modelsDescription}</p>
                        </article>
                        <article>
                            <UsersRound aria-hidden='true' />
                            <h3>{content.flexibilityTitle}</h3>
                            <p>{content.flexibilityDescription}</p>
                        </article>
                    </div>
                </section>
            )}
        </>
    );
}

function DocumentPage({
    content,
    locale,
    messages,
}: {
    content: DocumentPageContent;
    locale: Locale;
    messages: SiteUiMessages;
}): ReactElement {
    return (
        <article className='site-document'>
            <header>
                <span className='eyebrow'>{content.eyebrow}</span>
                <h1>{content.title}</h1>
                <p>{content.description}</p>
                <p className='site-document__revision'>
                    {interpolateSiteMessage(messages.updatedAt, {
                        date: formatRevisionDate(content.updatedAt, locale),
                    })}
                </p>
            </header>
            {content.sections.map((section) => (
                <section key={section.title}>
                    <h2>{section.title}</h2>
                    {section.paragraphs.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                    ))}
                    {section.items !== undefined && (
                        <ul>
                            {section.items.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    )}
                </section>
            ))}
        </article>
    );
}

export function SiteApplication(): ReactElement {
    const route = resolveSiteRoute(window.location.pathname);
    const messages = siteUiMessages[route.locale];
    const homeRoute = routeForKey('home', route.locale);
    const toolHref = `/tool/?l=${route.locale}`;
    const themeLabels: Readonly<Record<ThemePreference, string>> = {
        system: messages.theme.system,
        light: messages.theme.light,
        dark: messages.theme.dark,
    };

    useEffect(() => {
        synchronizeMetadata(route);
    }, [route]);

    return (
        <div className='site-shell'>
            <header className='site-header'>
                <a
                    className='brand'
                    href={homeRoute.path}
                    aria-label={messages.home}
                >
                    <span className='brand-mark'>
                        <ScanFace aria-hidden='true' />
                    </span>
                    <span>FACE LAB</span>
                </a>
                <div className='site-controls'>
                    <ThemeControl
                        label={messages.theme.label}
                        labels={themeLabels}
                        nextLabel={(theme) =>
                            interpolateSiteMessage(messages.theme.next, { theme })
                        }
                    />
                    <div
                        className='locale-switch'
                        role='group'
                        aria-label={messages.language}
                    >
                        {(['en', 'tr'] as const).map((locale) => (
                            <a
                                key={locale}
                                href={localizedRouteFor(route, locale).path}
                                hrefLang={locale}
                                lang={locale}
                                aria-current={route.locale === locale ? 'true' : undefined}
                            >
                                {locale.toUpperCase()}
                            </a>
                        ))}
                    </div>
                    <a
                        className='site-button site-button--compact'
                        href={toolHref}
                    >
                        {messages.openTool} <ArrowRight aria-hidden='true' />
                    </a>
                </div>
            </header>

            <main className='site-main'>
                {route.content.kind === 'landing' ? (
                    <LandingPage
                        route={route}
                        content={route.content}
                        messages={messages}
                        toolHref={toolHref}
                        homeHref={homeRoute.path}
                    />
                ) : (
                    <DocumentPage
                        content={route.content}
                        locale={route.locale}
                        messages={messages}
                    />
                )}
            </main>
            <footer className='site-footer'>
                <span>Face Lab</span>
                <nav
                    className='site-footer__links'
                    aria-label={messages.legalNavigation}
                >
                    {legalPageKeys.map((key) => {
                        const legalRoute = routeForKey(key, route.locale);
                        return (
                            <a
                                key={key}
                                href={legalRoute.path}
                                aria-current={route.key === key ? 'page' : undefined}
                            >
                                {legalRoute.content.title}
                            </a>
                        );
                    })}
                    <a
                        href={parentSiteUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {messages.parentSite}
                    </a>
                </nav>
            </footer>
        </div>
    );
}
